import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ConnectorConfiguration } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ButtonComponent,
  ButtonIconComponent,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { updateFormArrayControlsLengthMutably } from '../../utils';

import {
  AirtableConfiguration,
  ConnectorForm,
  TableRow,
  TableRowForm,
} from './airtable-connector-dialog.models';

@UntilDestroy()
@Component({
  selector: 'blaze-airtable-connector-dialog',
  templateUrl: './airtable-connector-dialog.component.html',
  styleUrls: ['./airtable-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DialogModule,
    AsyncPipe,
    EnvironmentIndicatorComponent,
    CopyConnectorConfigEnvButtonComponent,
    ButtonBarComponent,
    IconsModule,
    ButtonIconComponent,
    ButtonComponent,
    ReactiveFormsModule,
    PanelComponent,
  ],
  host: { class: 'data-connector-dialog' },
})
export class AirtableConnectorDialogComponent
  extends BaseConnectorDialogComponent<AirtableConfiguration>
  implements OnInit
{
  private panelRef = inject(PanelRef);

  serviceName = 'Airtable';
  connectorForm: FormGroup<ConnectorForm>;
  isLoading = false;

  get tables(): FormArray<FormGroup<TableRowForm>> {
    return this.connectorForm.controls.tables;
  }

  get canRemoveTable(): boolean {
    return this.tables.length > 1;
  }

  constructor() {
    super();
    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      apiKey: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      baseId: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      tables: new FormArray<FormGroup<TableRowForm>>([this.createTableControls()], {
        validators: this.tablesValidatorFn,
      }),
    });
  }

  ngOnInit(): void {
    if (this.connector) {
      this.fillForm(this.getConfiguration(this.connector));
    }
  }

  getConfiguration(connector: ConnectorConfiguration | undefined): AirtableConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? {};

    const apiKey = String(configuration?.apiKey ?? '');
    const baseId = String(configuration?.baseId ?? '');
    const tables: TableRow[] = configuration?.tables
      ? (configuration?.tables as unknown as any[]).map((table) => ({
          name: table['Name'] ?? table['name'] ?? '',
          primaryFieldName: table['PrimaryFieldName'] ?? table['primaryFieldName'] ?? '',
        }))
      : [];

    return {
      connectorId,
      connectorName,
      apiKey,
      baseId,
      tables,
    };
  }

  createTableControls(name = '', primaryFieldName = ''): FormGroup<TableRowForm> {
    return new FormGroup({
      name: new FormControl(name, { nonNullable: true, validators: [Validators.required] }),
      primaryFieldName: new FormControl(primaryFieldName, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
  }

  addTable(): void {
    this.tables.push(this.createTableControls());
  }

  removeTable(index: number): void {
    this.tables.removeAt(index);
  }

  fillForm(configuration: AirtableConfiguration): void {
    const { connectorId } = configuration;

    this.connectorId = connectorId;

    updateFormArrayControlsLengthMutably(
      this.connectorForm.controls.tables,
      configuration.tables.length,
      () => this.createTableControls(),
    );

    this.connectorForm.patchValue(
      {
        connectorName: configuration.connectorName,
        apiKey: configuration.apiKey,
        baseId: configuration.baseId,
        tables: configuration.tables,
      },
      { emitEvent: false },
    );
  }

  saveConnector(): void {
    const { connectorName, baseId, apiKey, tables } = this.connectorForm.value;

    if (!connectorName || !baseId || !apiKey || !tables) {
      return;
    }

    this.isLoading = true;

    const action$ = !this.connectorId
      ? this.connectorsApiService.postCreateAirtableConnector({
          accountId: this.accountId,
          name: connectorName,
          apiKey,
          baseId,
          tables: tables.filter((table) => !!table.name) as any,
        })
      : this.connectorsApiService.putUpdateAirtableConnector(this.connectorId, {
          name: connectorName,
          apiKey,
          baseId,
          tables: tables.filter((table) => !!table.name) as any,
        });

    action$.pipe(untilDestroyed(this)).subscribe({
      next: ({ isSuccess, errors, connectorInfo }) => {
        this.isLoading = false;
        if (!isSuccess) {
          this.handleErrors(errors);
        } else {
          this.connectorsCollService.upsertOneInCache(connectorInfo);
          this.panelRef.close();
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.notificationService.showError(error);
      },
    });
  }

  private tablesValidatorFn(control: AbstractControl): ValidationErrors | null {
    const value = control.value as TableRow[];
    const isValid = value.some((v) => !!v.name);

    if (!isValid) {
      return { tables: 'At least on table must be defined' };
    }

    return null;
  }
}
